/**
 * Social-proof UI:
 * - Collapsed pill under WhatsApp (viewer count)
 * - Click pill → expand full bar
 * - Click X → collapse back to pill
 * - Live viewers (IST demand + soft drift)
 */
(function () {
  var bar = document.getElementById('ayudra-social-proof-bar');
  if (!bar) return;

  var viewersEnabled = bar.dataset.viewersEnabled !== 'false';
  var offerEnabled = bar.dataset.offerEnabled !== 'false';
  var expandBtn = bar.querySelector('[data-social-proof-expand]');
  var panel = bar.querySelector('[data-social-proof-panel]');
  var collapseBtns = bar.querySelectorAll('[data-social-proof-collapse]');

  function getIstParts() {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    var map = {};
    parts.forEach(function (p) {
      if (p.type !== 'literal') map[p.type] = p.value;
    });

    return {
      hour: parseInt(map.hour, 10) % 24,
      minute: parseInt(map.minute, 10) || 0
    };
  }

  function clearFabOffset() {
    document.documentElement.classList.remove('has-social-proof-bar');
    document.documentElement.classList.remove('is-social-proof-expanded');
    document.documentElement.style.removeProperty('--ayudra-fab-offset');
    document.querySelectorAll('[data-ayudra-fab-lifted="true"]').forEach(function (el) {
      el.style.bottom = '';
      el.removeAttribute('data-ayudra-fab-lifted');
    });
  }

  function setExpanded(expanded) {
    if (expanded) {
      bar.classList.remove('is-collapsed');
      bar.classList.add('is-expanded');
      if (panel) panel.hidden = false;
      if (expandBtn) expandBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('is-social-proof-expanded');
    } else {
      bar.classList.remove('is-expanded');
      bar.classList.add('is-collapsed');
      if (panel) panel.hidden = true;
      if (expandBtn) expandBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('is-social-proof-expanded');
    }
    updateFabOffset();
  }

  function initExpandCollapse() {
    if (expandBtn) {
      expandBtn.addEventListener('click', function (e) {
        e.preventDefault();
        setExpanded(true);
      });
    }

    collapseBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        setExpanded(false);
      });
    });
  }

  /* ---------------- Live viewers ---------------- */
  function initViewers() {
    if (!viewersEnabled) return;

    var countEls = bar.querySelectorAll('[data-live-viewers-count], [data-live-viewers-count-panel]');
    if (!countEls.length) return;

    var MAX = Math.max(50, parseInt(bar.dataset.max, 10) || 1500);
    var MIN = Math.max(8, parseInt(bar.dataset.min, 10) || 28);
    var SOFT_PEAK = 0.9;
    var STORAGE_KEY = 'ayudra_live_viewers_v4';
    var TICK_MS = 4500;
    var TARGET_REFRESH_MS = 28000;

    function demandFactor(hour, minute) {
      var t = hour + minute / 60;
      var points = [
        [0, 0.08],
        [2, 0.05],
        [4, 0.06],
        [6, 0.16],
        [8, 0.38],
        [10, 0.58],
        [12, 0.5],
        [14, 0.54],
        [16, 0.68],
        [18, 0.8],
        [20, 0.88],
        [21.5, 0.82],
        [23, 0.4],
        [24, 0.08]
      ];

      for (var i = 0; i < points.length - 1; i++) {
        var a = points[i];
        var b = points[i + 1];
        if (t >= a[0] && t <= b[0]) {
          var p = (t - a[0]) / (b[0] - a[0] || 1);
          p = p * p * (3 - 2 * p);
          return a[1] + (b[1] - a[1]) * p;
        }
      }
      return 0.2;
    }

    function clamp(n, min, max) {
      return Math.min(max, Math.max(min, n));
    }

    function rand(min, max) {
      return min + Math.random() * (max - min);
    }

    function formatCount(n) {
      return Math.round(n).toLocaleString('en-IN');
    }

    function bandForNow() {
      var ist = getIstParts();
      var demand = demandFactor(ist.hour, ist.minute);
      var center = MIN + (MAX - MIN) * demand * SOFT_PEAK;
      center = clamp(center, MIN, MAX - 60);
      var spread = Math.max(35, center * 0.06);
      return {
        floor: Math.round(clamp(center - spread, MIN, MAX)),
        ceiling: Math.round(clamp(center + spread, MIN, MAX - 40)),
        center: Math.round(center)
      };
    }

    function computeTarget(nearCurrent) {
      var band = bandForNow();
      if (typeof nearCurrent === 'number') {
        return Math.round(clamp(nearCurrent + rand(-12, 12), band.floor, band.ceiling));
      }
      return Math.round(clamp(band.center + rand(-18, 18), band.floor, band.ceiling));
    }

    function loadState() {
      try {
        var raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }

    function saveState(count, target) {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ count: count, target: target, ts: Date.now() })
        );
      } catch (e) {
        /* ignore */
      }
    }

    var band = bandForNow();
    var target = computeTarget();
    var current;
    var saved = loadState();

    if (saved && typeof saved.count === 'number' && Date.now() - (saved.ts || 0) < 10 * 60 * 1000) {
      current = clamp(saved.count, band.floor, band.ceiling);
      target = clamp(saved.target || target, band.floor, band.ceiling);
    } else {
      current = clamp(target + rand(-8, 8), band.floor, band.ceiling);
    }

    function render() {
      var text = formatCount(current);
      countEls.forEach(function (el) {
        el.textContent = text;
      });
    }

    function tick() {
      band = bandForNow();
      target = clamp(target, band.floor, band.ceiling);
      current = clamp(current, band.floor, band.ceiling);

      var gap = target - current;
      var step = 0;
      if (gap !== 0) {
        var direction = gap > 0 ? 1 : -1;
        var magnitude = Math.min(3, Math.max(1, Math.round(Math.abs(gap) * 0.15)));
        step = direction * magnitude;
      }

      if (Math.random() < 0.45) {
        step += Math.random() < 0.5 ? -1 : 1;
      }

      step = clamp(step, -4, 4);
      current = clamp(current + step, band.floor, band.ceiling);
      render();
      saveState(current, target);
    }

    function refreshTarget() {
      target = computeTarget(current);
      saveState(current, target);
    }

    render();
    setInterval(tick, TICK_MS);
    setInterval(refreshTarget, TARGET_REFRESH_MS);
  }

  initExpandCollapse();
  initViewers();

  if (!viewersEnabled && !offerEnabled) {
    bar.hidden = true;
    clearFabOffset();
    return;
  }

  bar.hidden = false;
  bar.classList.add('is-ready');
  setExpanded(false);

  function getWhatsAppCandidates() {
    var selectors = [
      '#es-whatsapp-chat-embed a',
      '#es-whatsapp-chat-embed',
      '#whatsapp-chat-widget',
      '.whatsapp-chat-widget',
      '.wa-widget',
      '.wa__btn_popup',
      '[id*="whatsapp" i]',
      '[class*="whatsapp" i]',
      '[class*="WhatsApp" i]',
      'a[href*="wa.me"]',
      'a[href*="api.whatsapp.com"]',
      'a[aria-label*="WhatsApp" i]',
      'a[aria-label*="Chat with us" i]',
      'button[aria-label*="WhatsApp" i]'
    ];
    var found = [];
    selectors.forEach(function (sel) {
      try {
        document.querySelectorAll(sel).forEach(function (el) {
          found.push(el);
        });
      } catch (e) {
        /* ignore */
      }
    });
    return found;
  }

  function resolveFixedHost(el) {
    var node = el;
    var depth = 0;
    while (node && node !== document.body && depth < 8) {
      var style = window.getComputedStyle(node);
      if (style.position === 'fixed') return node;
      node = node.parentElement;
      depth += 1;
    }
    return null;
  }

  function readCssPx(name, fallback) {
    var raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    var n = parseFloat(raw);
    return isNaN(n) ? fallback : n;
  }

  var moTimer = null;
  var lastFabOffset = '';
  var updatingFab = false;

  function updateFabOffset() {
    if (updatingFab) return;
    if (bar.hidden || !bar.classList.contains('is-ready')) {
      clearFabOffset();
      lastFabOffset = '';
      return;
    }

    updatingFab = true;
    try {
      var expanded = bar.classList.contains('is-expanded');
      var gap = 12;
      var offset;

      if (expanded) {
        var rect = bar.getBoundingClientRect();
        offset = Math.max(88, Math.ceil(window.innerHeight - rect.top + gap));
        document.documentElement.classList.add('is-social-proof-expanded');
      } else {
        var pill = expandBtn || bar;
        var pillHeight = Math.ceil(pill.getBoundingClientRect().height || 36);
        var fabBottom = readCssPx('--ayudra-fab-bottom', 20);
        if (document.documentElement.classList.contains('has-sticky-atc')) {
          fabBottom = Math.max(
            fabBottom,
            readCssPx('--ayudra-sticky-atc-offset', fabBottom)
          );
        }
        offset = Math.ceil(fabBottom + pillHeight + gap);
        document.documentElement.classList.remove('is-social-proof-expanded');
      }

      var offsetValue = offset + 'px';
      document.documentElement.classList.add('has-social-proof-bar');

      if (offsetValue !== lastFabOffset) {
        lastFabOffset = offsetValue;
        document.documentElement.style.setProperty('--ayudra-fab-offset', offsetValue);
      }

      getWhatsAppCandidates().forEach(function (el) {
        var host = resolveFixedHost(el) || el;
        var style = window.getComputedStyle(host);
        if (style.position !== 'fixed') return;

        var hostRect = host.getBoundingClientRect();
        if (hostRect.bottom < window.innerHeight - 200) return;

        if (host.style.bottom !== offsetValue) {
          host.style.setProperty('bottom', offsetValue, 'important');
        }
        host.setAttribute('data-ayudra-fab-lifted', 'true');
      });
    } finally {
      updatingFab = false;
    }
  }

  updateFabOffset();
  window.addEventListener('resize', function () {
    if (moTimer) clearTimeout(moTimer);
    moTimer = setTimeout(updateFabOffset, 120);
  });
  window.addEventListener(
    'scroll',
    function () {
      if (moTimer) clearTimeout(moTimer);
      moTimer = setTimeout(updateFabOffset, 150);
    },
    { passive: true }
  );
  setTimeout(updateFabOffset, 400);
  setTimeout(updateFabOffset, 1200);

  var mo = new MutationObserver(function () {
    if (updatingFab) return;
    if (moTimer) clearTimeout(moTimer);
    moTimer = setTimeout(updateFabOffset, 120);
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
